/**
 * Message Area Component
 * Center panel containing messages and input (header moved to UnifiedHeader)
 * Handles channel-wide drag & drop with Viber-style split drop zones
 *
 * Drag tracking uses a timeout-based approach (like react-dropzone):
 * - dragOver fires continuously while dragging → keeps overlay visible
 * - dragLeave starts a short timer → cancelled by next dragOver
 * - Only when drag truly leaves the area (no more dragOver) does the timer fire
 * This avoids the classic blink bug caused by enter/leave counters.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/store/app.store';
import { useChannel, useMarkChannelAsRead } from '@/hooks/useChannels';
import { useDMThread, useMarkDMThreadAsRead } from '@/hooks/useDMs';
import MessageList from '@/components/messages/MessageList';
import MessageInput from '@/components/messages/MessageInput';
import TypingIndicator from '@/components/messages/TypingIndicator';
import { AddMemberModal } from '@/components/channels';
import DropZoneOverlay from '@/components/attachments/DropZoneOverlay';
import { isCompressibleMedia } from '@/utils/media-metadata.util';
import type { Message } from '@/types/api.types';

const MessageArea = () => {
  const { activeChannelId, activeDMThreadId } = useAppStore();
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>();
  const [droppedSendOption, setDroppedSendOption] = useState<'image' | 'file' | undefined>();
  const dragLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get channel info if a channel is selected
  const { data: channel } = useChannel(activeChannelId);

  // Get DM thread info if a DM is selected
  useDMThread(activeDMThreadId);

  // Mark-as-read mutations
  const markChannelAsRead = useMarkChannelAsRead();
  const markDMThreadAsRead = useMarkDMThreadAsRead();

  // Auto mark-as-read when switching channels/DMs
  useEffect(() => {
    if (activeChannelId) {
      const timer = setTimeout(() => {
        markChannelAsRead.mutate(activeChannelId);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (activeDMThreadId) {
      const timer = setTimeout(() => {
        markDMThreadAsRead.mutate(activeDMThreadId);
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannelId, activeDMThreadId]);

  // Clear reply state when switching channels/DMs
  useEffect(() => {
    setReplyToMessage(null);
  }, [activeChannelId, activeDMThreadId]);

  // Cleanup drag leave timer on unmount
  useEffect(() => {
    return () => {
      if (dragLeaveTimerRef.current) clearTimeout(dragLeaveTimerRef.current);
    };
  }, []);

  const handleReply = (message: Message) => {
    setReplyToMessage(message);
  };

  const handleCancelReply = () => {
    setReplyToMessage(null);
  };

  // Cancel any pending drag-leave timer
  const cancelDragLeaveTimer = () => {
    if (dragLeaveTimerRef.current) {
      clearTimeout(dragLeaveTimerRef.current);
      dragLeaveTimerRef.current = null;
    }
  };

  // dragOver fires continuously while hovering — keep overlay visible
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    cancelDragLeaveTimer();
    if (!isDraggingOver && e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  }, [isDraggingOver]);

  // dragEnter: show overlay immediately for file drags
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    cancelDragLeaveTimer();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  }, []);

  // dragLeave: start a short timer — if dragOver fires again (child transition),
  // the timer gets cancelled. Only fires when truly leaving the area.
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    cancelDragLeaveTimer();
    dragLeaveTimerRef.current = setTimeout(() => {
      setIsDraggingOver(false);
    }, 80);
  }, []);

  // Fallback drop handler — processes files if overlay zones didn't catch the drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    cancelDragLeaveTimer();
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Default to 'image' for media files, 'file' for others
      const hasMedia = files.some((f) => isCompressibleMedia(f));
      setDroppedFiles(files);
      setDroppedSendOption(hasMedia ? 'image' : 'file');
    }
  }, []);

  const handleExternalFilesHandled = useCallback(() => {
    setDroppedFiles(undefined);
    setDroppedSendOption(undefined);
  }, []);

  // No channel or DM selected
  if (!activeChannelId && !activeDMThreadId) {
    return (
      <div className="flex items-center justify-center h-full bg-[#fafbfc] dark:bg-dark-bg-primary">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-500/10 flex items-center justify-center animate-gentle-pulse">
            <svg
              className="h-8 w-8 text-primary-500 dark:text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Избери канал
          </h3>
          <p className="mt-1 text-sm text-gray-400 dark:text-dark-text-muted">
            Започни комуникация като избереш канал от страничната лента
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col h-full bg-[#fafbfc] dark:bg-dark-bg-primary"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay — always mounted, visibility via CSS */}
      <DropZoneOverlay
        visible={isDraggingOver}
        onDrop={(files, sendOption) => {
          cancelDragLeaveTimer();
          setIsDraggingOver(false);
          setDroppedFiles(files);
          setDroppedSendOption(sendOption);
        }}
      />

      {/* Messages List Area */}
      <MessageList onReply={handleReply} />

      {/* Typing Indicator */}
      <TypingIndicator conversationId={activeChannelId || activeDMThreadId} />

      {/* Message Input Area */}
      <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-dark-border-primary bg-white dark:bg-dark-bg-secondary">
        <div className="max-w-[960px] mx-auto w-full">
          <MessageInput
            replyToMessage={replyToMessage}
            onCancelReply={handleCancelReply}
            externalFiles={droppedFiles}
            externalSendOption={droppedSendOption}
            onExternalFilesHandled={handleExternalFilesHandled}
          />
        </div>
      </div>

      {/* Add Member Modal */}
      {channel && isAddMemberModalOpen && (
        <AddMemberModal
          channel={channel}
          onClose={() => setIsAddMemberModalOpen(false)}
          onMembersAdded={() => {
            setIsAddMemberModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default MessageArea;
